import { useEffect, useState, useRef } from "react";
import { captureMessage } from "../lib/utils";

// Size thresholds for warnings (in bytes)
const SIZE_WARNING_THRESHOLD = 512 * 1024; // 512KB
const SIZE_CRITICAL_THRESHOLD = 1024 * 1024; // 1MB

/**
 * Hook for storing values in sessionStorage with optional localStorage backup.
 *
 * The returned setValue function accepts a second parameter (backupToLocalStorage):
 * - When false (default): Saves to sessionStorage only, clears localStorage
 * - When true: Saves to both sessionStorage and localStorage (for crash recovery)
 *
 * On initialization, always tries sessionStorage first, then falls back to localStorage
 * if sessionStorage is empty (useful for crash recovery).
 *
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if nothing stored
 */
export const useSessionStorage = (key, defaultValue = null) => {
  const lastWarningSizeRef = useRef(0);

  const [value, setValue] = useState(() => {
    try {
      // Try sessionStorage first (normal case)
      const stored = sessionStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored);
      }
      // Fall back to localStorage (crash recovery case)
      const localStored = localStorage.getItem(key);
      if (localStored) {
        console.log(`Restoring ${key} from crash recovery backup`);
        return JSON.parse(localStored);
      }
      return defaultValue;
    }
    catch(error) {
      console.debug(`Could not retrieve from session/local storage: ${error}`);
      return defaultValue;
    }
  });

  // Save to sessionStorage whenever value changes
  useEffect(() => {
    try {
      const jsonValue = JSON.stringify(value);
      sessionStorage.setItem(key, jsonValue);
    }
    catch(error) {
      console.debug(`Could not save to sessionStorage: ${error}`);
    }
  }, [key, value]);

  // Enhanced setValue that accepts optional localStorage backup flag
  const setValueWithBackup = (newValue, backupToLocalStorage = false) => {
    setValue(newValue);

    // Track size for conversations to detect memory bloat
    if (key.startsWith('conversation.') && newValue?.turns) {
      try {
        const jsonValue = JSON.stringify(newValue);
        const sizeBytes = new Blob([jsonValue]).size;
        const sizeKB = Math.round(sizeBytes / 1024);

        // Only log when conversation size crosses significant thresholds
        const shouldWarn = sizeBytes >= SIZE_WARNING_THRESHOLD &&
                          (lastWarningSizeRef.current === 0 ||
                           sizeBytes - lastWarningSizeRef.current >= 100 * 1024);

        if (shouldWarn) {
          lastWarningSizeRef.current = sizeBytes;

          if (sizeBytes >= SIZE_CRITICAL_THRESHOLD) {
            console.warn(`⚠️ Large conversation detected (${sizeKB}KB). This may cause browser performance issues.`);
            captureMessage("Large conversation size", "warning", {
              key,
              sizeBytes,
              sizeKB,
              turnCount: newValue.turns.length,
            });
          } else {
            console.info(`Conversation size: ${sizeKB}KB (${newValue.turns.length} turns)`);
          }
        }
      } catch (e) {
        console.debug(`Could not measure conversation size: ${e}`);
      }
    }

    // Handle localStorage based on flag
    if (backupToLocalStorage) {
      try {
        if (newValue && (typeof newValue !== 'object' || Object.keys(newValue).length > 0)) {
          localStorage.setItem(key, JSON.stringify(newValue));
        } else {
          localStorage.removeItem(key);
        }
      } catch (e) {
        console.debug(`Could not save to localStorage: ${e}`);
      }
    } else {
      // Clear localStorage when not backing up
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.debug(`Could not remove from localStorage: ${e}`);
      }
    }
  };

  return [value, setValueWithBackup];
};
